<template>
  <RowGrid class="LaptopMatched" :columns="['12rem', '1fr', '12rem']">
    <div>
      <LaptopImage
        :laptop="original" />
      <TagMatchScore
        :score="original.matchScore" />
    </div>
    <DiffLaptops
      :doCompactMatched="true"
      :incoming="incomingCompact"
      :original="originalCompact"
      @accept="acceptSpec" />
    <div class="sideActions">
      <Btn
        v-if="canAssign"
        class="success light"
        :isLoading="isAssigning"
        @click="assign">
        {{ $t('assign') }}
      </Btn>
      <Notice v-else class="success">
        <Icon name="check" />
        {{ $t('assigned') }}
      </Notice>
      <Btn
        class="light"
        :isLoading="isSaving"
        @click="openModal">
        {{ $t('edit') }}
      </Btn>
      <ModalForm
        v-if="isModalOpen"
        @close="isModalOpen = false"
        @submit="patchLaptop(originalStatic)">
        <FormLaptop
          :laptop.sync="originalStatic" />
        <Btn :isLoading="isSaving" type="submit">
          {{ $t('update') }}
        </Btn>
      </ModalForm>
      <a
        v-if="original.asin"
        class="Btn light"
        :href="formAmazonLink(original.asin)"
        rel="noreferrer noopener"
        target="_blank">
        {{ $t('amazon') }}
      </a>
      <Route
        :id="original.id"
        class="Btn light"
        to="Laptop">
        {{ $t('open') }}
      </Route>
    </div>
  </RowGrid>
</template>

<script>
import { actions, getters } from '@utils'
import { assignSpec, detach } from '@utils/objects'
import { map, modifyPath, omit, pick, propEq } from 'rambda'
import DiffLaptops from '@components/DiffLaptops'
import FormLaptop from '@components/forms/FormLaptop'
import LaptopImage from '@components/LaptopImage'
import ModalForm from '@components/modals/ModalForm'
import TagMatchScore from './TagMatchScore'
import { pluckTruthy } from '@utils/collections'

export default {
  components: {
    DiffLaptops,
    FormLaptop,
    LaptopImage,
    ModalForm,
    TagMatchScore,
  },
  props: {
    incoming: { type: Object, required: true },
    original: { type: Object, required: true },
  },
  data() {
    return {
      isAssigned: false,
      isAssigning: false,

      isModalOpen: false,
      isSaving: false,
      originalStatic: null,
    }
  },
  computed: {
    canAssign() {
      const { url } = this.incoming.reviewsExternal[0]

      return !this.isAssigned &&
        !this.original.reviewsExternal.some(propEq('url', url))
    },
    incomingCompact() {
      return compact(this.incoming)
    },
    originalCompact() {
      return compact(this.original)
    },
    ...getters('formAmazonLink'),
  },
  methods: {
    async acceptSpec({ key, value }) {
      this.original[key] = value

      await this.$wrap(
        () => this.PATCH_LAPTOP({
          id: this.original.id,
          [key]: value,
        }),
        'isSaving',
      )
    },
    assign() {
      const incoming = modifyPath(
        ['reviewsExternal'],
        map(assignSpec({
          matchScore: () => this.original.matchScore,
          laptop: () => omit(['id', 'reviewsExternal'], this.incoming),
        })),
        this.incoming,
      )

      const reviewsExternalLaptops = pluckTruthy(
        'laptop',
        this.original.reviewsExternal,
      )

      return this.$wrap(
        async () => {
          const merged = await this.CREATE_PRODUCT_MERGE({
            original: this.original,
            incoming: [...reviewsExternalLaptops, incoming],
          })

          await this.PATCH_LAPTOP({
            id: this.original.id,
            ...merged,
          })

          this.isAssigned = true
        },
        'isAssigning',
      )
    },
    openModal() {
      this.originalStatic = detach(this.original)
      this.isModalOpen = true
    },
    patchLaptop(laptop) {
      return this.$wrap(
        async () => {
          await this.PATCH_LAPTOP(laptop)
          this.$up('original', {
            ...this.original,
            ...laptop,
          })
          this.isModalOpen = false
        },
        'isSaving',
      )
    },
    ...actions(
      'CREATE_PRODUCT_MERGE',
      'PATCH_LAPTOP',
    ),
  },
}

const compact = pick([
  'name',

  'cpu',
  'gpu',
  'ssdAmount',

  'screen',
  'screenHz',
  'resolution',

  'batteryWh',
])
</script>

<style lang="scss">
.LaptopMatched {
  margin-bottom: $u;
  padding: $u;

  > .sideActions {
    .Btn {
      margin-bottom: $uh;
    }
  }
}
</style>

<i18n>
{
  "en": {
    "amazon": "Amazon",
    "assign": "Assign review",
    "assigned": "Assigned",
    "edit": "Edit",
    "open": "Open",
    "update": "Update"
  }
}
</i18n>
